import { action, observable } from 'mobx'; // computed
import moment from 'moment';
import type { UpgradePageType } from 'modules/Upgrade';
import { OpenPageId } from 'utils/withProgressForm';
import { handleValuesParams } from './helper';

const CACHE_KEY = '__TIGER_PORTAL_ACCOUNTS_PARAMS__';

/**
 * 用来保存和同步每一步表单数据的store
 */
export class ValuesStore {
    @observable
    showProgressBar = true;

    @observable
    total = 0;

    @observable
    step = 0;

    @observable
    title: string | null = null;

    // 被改变的的 key集合，开第二个户中记录当前key的内容是否被更新过，该key与 mergeParams 对应
    @observable
    changedKeys: Partial<Record<UpgradePageType, boolean>> = {};

    /**
     * setStep 设置开户进度
     *
     * @param {Number} step 开户进度
     */
    @action
    setStep(step: number) {
        this.step = step;
    }

    @action
    setTotalStep(totalStep: number) {
        this.total = totalStep;
    }

    @action
    setShowProgressBar(show: boolean) {
        this.showProgressBar = show;
    }

    /**
     * setTitle 设置标题
     *
     * @param {String} title 页面标题
     */
    @action
    setTitle(title: string) {
        this.title = title;
    }

    /**
     * reset 初始化store
     * @param {Number} total 总步骤数
     */
    @action
    init(total: number, initialData: object, cacheId: string) {
        this.total = total;
        this.CACHE_KEY = cacheId;

        if (initialData) {
            this.paramsMap = initialData;
        }

        this.resetChanged();

        // 初始化的时候就将数据缓存到本地
        sessionStorage.setItem(this.CACHE_KEY, JSON.stringify(this.paramsMap));
    }

    CACHE_KEY: string = CACHE_KEY;

    paramsMap: Partial<Record<OpenPageId, any>> = {};
    mergeParams(key, params) {
        this.paramsMap[`${key}`] = params;
        this.changedKeys[key] = true;

        // 缓存到本地
        sessionStorage.setItem(this.CACHE_KEY, JSON.stringify(this.paramsMap));
    }

    /**
     * getParams
     *
     * @param {string} [key] 页面标识
     * @returns {Object} 指定页面或者全部页面的表单项集合
     */
    getParams<T = any>(key?: OpenPageId): T {
        if (typeof key !== 'undefined') {
            return this.paramsMap[`${key}`];
        }

        return handleValuesParams(this.paramsMap);
    }

    // 清理缓存
    clear() {
        this.paramsMap = {};
        sessionStorage.removeItem(this.CACHE_KEY);
    }

    // 获取年龄
    getAge() {
        const userinfo = this.getParams();

        if (userinfo.birthday) {
            return moment().diff(userinfo.birthday, 'years');
        }

        if (userinfo.id_no) {
            return moment().diff(moment(userinfo.id_no.substring(6, 14), 'YYYYMMDD'), 'years');
        }

        return 30;
    }

    /**
     * @description 重置changed状态
     */
    @action
    resetChanged() {
        this.changedKeys = {};
    }

    /**
     * @description 开第二个户中查询某个key的内容是否被修改过
     */
    isChanged(key: UpgradePageType) {
        return !!this.changedKeys[key];
    }
}

const store = new ValuesStore();

export default store;
export * from './helper';
